import { forwardRef, PropsWithoutRef } from 'react';
import { useDefaultFormContext } from './useDefaultFormValues';
import { ZeroInvoiceTotalForm } from './ZeroInvoiceTotalForm/ZeroInvoiceTotalForm';
import { ProcessPaymentType } from './ProcessPaymentType';
import { GatewayPaymentForm } from './GatewayPaymentForm/GatewayPaymentForm';
import { MewsTerminalPaymentForm } from './MewsTerminalPayment/MewsTerminalPaymentForm';
import { PaymentRequestForm } from './PaymentRequestForm/PaymentRequestForm';
import { ExternalCardPaymentForm } from './ExternalCardPaymentForm/ExternalCardPaymentForm';
import { CashPaymentForm } from './CashPaymentForm/CashPaymentForm';
import { ExternalPaymentForm } from './ExternalPaymentForm/ExternalPaymentForm';
import { TokenizeCardForm } from './TokenizeCardForm/TokenizeCardForm';
import { RecordCardForm } from './RecordCardForm/RecordCardForm';
import { GatewayPreauthorizationForm } from './GatewayPreauthorizationForm/GatewayPreauthorizationForm';
import { ExternalPreauthorizationForm } from './ExternalPreauthorizationForm/ExternalPreauthorizationForm';
import { MewsTerminalPreauthorizationForm } from './MewsTerminalPreauthorizationForm/MewsTerminalPreauthorizationForm';
import { PaymentFormProps } from './types';
import { useGetTotalOfSelectedInvoicesSuspense } from './hooks';

const paymentDialog = {
    [ProcessPaymentType.GatewayPayment]: GatewayPaymentForm,
    [ProcessPaymentType.MewsTerminalPayment]: MewsTerminalPaymentForm,
    [ProcessPaymentType.PaymentRequest]: PaymentRequestForm,
    [ProcessPaymentType.ExternalCardPayment]: ExternalCardPaymentForm,
    [ProcessPaymentType.CashPayment]: CashPaymentForm,
    [ProcessPaymentType.ExternalPayment]: ExternalPaymentForm,
    [ProcessPaymentType.TokenizeCard]: TokenizeCardForm,
    [ProcessPaymentType.RecordCard]: RecordCardForm,
    [ProcessPaymentType.GatewayCardPreauth]: GatewayPreauthorizationForm,
    [ProcessPaymentType.ExternalCardPreauth]: ExternalPreauthorizationForm,
    [ProcessPaymentType.MewsTerminalPreauth]: MewsTerminalPreauthorizationForm,
};

type Props = PaymentFormProps & {
    paymentType: ProcessPaymentType;
};

const ProcessPaymentTypeForm = forwardRef(({ paymentType, ...formProps }: PropsWithoutRef<Props>, ref) => {
    const TypeForm = paymentDialog[paymentType];

    return <TypeForm {...formProps} ref={ref} />;
});

const ZeroInvoiceTotalFormWrapper = forwardRef(({ paymentType, ...formProps }: PropsWithoutRef<Props>, ref) => {
    const {
        accountId,
        ownerIds,
        defaultFormValues: { invoiceIds, currencyCode },
    } = useDefaultFormContext();

    const invoiceTotal = useGetTotalOfSelectedInvoicesSuspense({
        accountId,
        ownerIds,
        currencyCode,
        selectedInvoiceIds: invoiceIds,
    });

    if (invoiceTotal === 0) {
        return <ZeroInvoiceTotalForm {...formProps} ref={ref} />;
    }

    return <ProcessPaymentTypeForm {...formProps} ref={ref} paymentType={paymentType} />;
});

export const ProcessPaymentForm = forwardRef(({ paymentType, ...formProps }: PropsWithoutRef<Props>, ref) => {
    const {
        defaultFormValues: { invoiceIds },
    } = useDefaultFormContext();

    if (invoiceIds && invoiceIds?.length > 0) {
        return <ZeroInvoiceTotalFormWrapper {...formProps} ref={ref} paymentType={paymentType} />;
    }

    return <ProcessPaymentTypeForm {...formProps} ref={ref} paymentType={paymentType} />;
});
